<template>
    <div>
        <b-container fluid>
            <h3 class="mt-3 mb-3">Bulk Edit — Default Picking Location</h3>

            <!-- Filters -->
            <div class="search_bar mb-3">
                <b-row>
                    <b-col cols="2" class="pr-1">
                        <b-form-select size="sm" v-model="search_criteria.channel_selected"
                            :options="channel_options" />
                    </b-col>
                    <b-col cols="4" class="pr-1">
                        <b-form-input id="kw" size="sm" type="text"
                            placeholder="Product SKU / Channel SKU / Description" v-model="search_criteria.keyword"
                            @keyup.enter="onSearch" />
                    </b-col>
                    <b-col class="text-left p-0">
                        <b-button class="btn-action" size="sm" variant="info" @click="onSearch">
                            <font-awesome-icon class="mr-1" icon="search" />Search
                        </b-button>

                        <b-button class="btn-action ml-2" size="sm" variant="primary" :disabled="selectedCount === 0"
                            @click="openBulkModal">
                            <font-awesome-icon class="mr-1" icon="edit" />
                            Bulk Edit ({{ selectedCount }})
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <!-- Pagination (top) -->
            <b-pagination size="sm" :total-rows="total_rows" v-model="currentPage" :per-page="TABLE_LIMIT"
                @change="onPageChange" :limit="PAGING_LIMIT" />

            <!-- Table -->
            <b-table responsive small outlined striped hover sticky-header="500px" :items="items" :fields="fields">
                <!-- Header checkbox -->
                <template v-slot:head(chk_box)>
                    <b-form-checkbox size="sm" plain id="chkbox_all" :indeterminate="isIndeterminate"
                        v-model="check_all_rows" @change="toggleSelectAll" />
                </template>

                <!-- Row checkbox -->
                <template v-slot:cell(chk_box)="data">
                    <b-form-checkbox size="sm" class="chkbox_row" plain :id="'chkbox_row_' + data.index"
                        v-model="data.item.is_checked" @change="onRowCheckChange" />
                </template>

                <template v-slot:cell(product_sku)="data">
                    <code>{{ data.item.product_sku }}</code>
                </template>

                <template v-slot:cell(channel_sku)="data">
                    <span>{{ data.item.channel_sku || "-" }}</span>
                </template>

                <template v-slot:cell(product_description_en)="data">
                    <span>{{ data.item.product_description_en }}</span>
                </template>

                <template v-slot:cell(default_picking_location)="data">
                    <span class="font-weight-500">{{ data.item.default_picking_location || "-" }}</span>
                </template>
                <template v-slot:cell(action)="data">
                    <b-button size="sm" variant="outline-primary" @click="openSingleEdit(data.item)">
                        Edit
                    </b-button>
                </template>
            </b-table>

            <!-- Pagination (bottom) -->
            <b-pagination size="sm" :total-rows="total_rows" v-model="currentPage" :per-page="TABLE_LIMIT"
                @change="onPageChange" :limit="PAGING_LIMIT" />
        </b-container>

        <b-modal id="bulkEditModal" ref="bulkEditModal"
            :title="modal.mode === 'single' ? 'Edit — Default Picking Location' : 'Bulk Edit — Default Picking Location'"
            @shown="focusModalInput" hide-footer>
            <b-form @submit.prevent="submitEdit">
                <b-form-group label="New Default Picking Location" label-for="newLoc" :description="modal.mode === 'single'
                    ? 'Leave blank to clear the location for this item.'
                    : 'Leave blank to clear the location for all selected items.'">
                    <b-form-input id="newLoc" v-model.trim="modal.newLocation" placeholder="e.g. Z1-A2-3B"
                        ref="modalInput" />
                </b-form-group>

                <div class="d-flex justify-content-between align-center">
                    <div>
                        <small class="text-muted">
                            <template v-if="modal.mode === 'single'">
                                Editing <strong>1</strong> item.
                            </template>
                            <template v-else>
                                Applying to <strong>{{ selectedCount }}</strong> selected item(s).
                            </template>
                        </small>
                    </div>
                    <div>
                        <b-button variant="secondary" @click="$refs.bulkEditModal.hide()">Cancel</b-button>
                        <b-button class="ml-2" variant="primary" :disabled="isSubmitting" type="submit">
                            {{ isSubmitting ? 'Saving…' : (modal.mode === 'single' ? 'Save' : 'Apply') }}
                        </b-button>
                    </div>
                </div>
            </b-form>
        </b-modal>

    </div>
</template>

<script>
import moment from "moment"; // kept for parity; remove if unused
import apiService from "../services/ApiService";
import global_function from "../mixins/global_function";

const fields = [
    { key: "chk_box", label: "" },
    { key: "product_sku", label: "Product SKU" },
    { key: "default_picking_location", label: "Default Picking Location" },
    { key: "action", label: "Action" }, // <- NEW
    { key: "channel_sku", label: "Channel SKU" },
    { key: "product_description_en", label: "Item Description" },

];


export default {
    name: "ProductLocationBulk",
    inject: ["API_HOST", "TABLE_LIMIT", "PAGING_LIMIT"],
    mixins: [apiService, global_function],
    data() {
        return {
            loader: false,
            items: [],
            fields,
            currentPage: 1,
            table_offset: 0,
            total_rows: 0,
            search_criteria: {
                keyword: "",
                channel_selected: null
            },
            channel_options: [{ text: "All Channel", value: null }],
            check_all_rows: false,
            isSubmitting: false,
            modal: {
                mode: "bulk",          // "bulk" | "single"
                targetSkus: [],        // array of product SKUs to update
                newLocation: ""
            }

        };
    },
    computed: {
        selectedCount() {
            return this.items.filter((x) => x.is_checked).length;
        },
        isIndeterminate() {
            const c = this.selectedCount;
            return c > 0 && c < this.items.length;
        }
    },
    async created() {
        await this.fetchList();
        const channels_response = await this.getChannels(this.$store.state.user_obj);
        for (const c of channels_response.channels) {
            this.channel_options.push({ value: c.name, text: c.name });
        }
    },
    mounted() {
        this.loader = this.$loading.show({ isFullPage: true, canCancel: false });
    },
    methods: {
        focusModalInput() {
            this.$nextTick(() => this.$refs.modalInput && this.$refs.modalInput.focus());
        },

        // API: list
        async fetchList() {
            const resp = await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "product_master_get.php",
                data: {
                    offset: this.table_offset,
                    limit: this.TABLE_LIMIT,
                    search_criteria: this.search_criteria,
                    client_id: this.$store.state.user_obj.client_id,
                    // (optional) backend may skip heavy fields to speed up
                    columns: [
                        "id",
                        "product_sku",
                        "channel_sku",
                        "product_description_en",
                        "default_picking_location"
                    ]
                }
            });
            // normalize & show
            this.items = (resp.data.products || []).map((p) => ({ ...p, is_checked: false }));
            this.total_rows = parseInt(resp.data.total_rows || 0);
            if (this.loader) this.loader.hide();
        },

        onSearch() {
            this.currentPage = 1;
            this.table_offset = 0;
            this.fetchList();
        },

        onPageChange(page) {
            this.currentPage = page;
            this.table_offset = (page - 1) * this.TABLE_LIMIT;
            this.fetchList();
        },

        toggleSelectAll(checked) {
            // select/deselect only current page items
            for (const it of this.items) it.is_checked = !!checked;
        },

        onRowCheckChange() {
            // keep header checkbox in sync
            if (this.selectedCount === this.items.length) this.check_all_rows = true;
            else if (this.selectedCount === 0) this.check_all_rows = false;
            else this.check_all_rows = false; // remains indeterminate via computed
        },

        openBulkModal() {
            if (this.selectedCount === 0) {
                this.$eventHub.$emit("show_modal_message", {
                    message_content: "Please select at least one row."
                });
                return;
            }
            this.modal.mode = "bulk";
            this.modal.targetSkus = this.items
                .filter(function (x) { return x.is_checked; })
                .map(function (x) { return x.product_sku; });
            this.modal.newLocation = "";
            this.$refs.bulkEditModal.show();
        },


        openSingleEdit(item) {
            this.modal.mode = "single";
            this.modal.targetSkus = [item.product_sku];
            this.modal.newLocation = item.default_picking_location || "";
            this.$refs.bulkEditModal.show();
        },

        async submitEdit() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;

            var skus = this.modal.targetSkus && this.modal.targetSkus.length ? this.modal.targetSkus : [];
            if (skus.length === 0) {
                this.$eventHub.$emit("show_modal_message", {
                    message_content: "No items selected.",
                    message_type: "error"
                });
                this.isSubmitting = false;
                return;
            }

            var payload = {
                client_id: this.$store.state.user_obj.client_id,
                user_id: this.$store.state.user_obj.user_id,
                product_skus: skus,                             // <— use SKUs
                new_default_picking_location: this.modal.newLocation || ""
            };

            var loaderX = this.$loading.show({ isFullPage: true, canCancel: false });

            try {
                var resp = await this.$http({
                    method: "POST",
                    baseURL: this.API_HOST + "",
                    url: "product_master_bulk_update_picking_location.php",
                    data: payload
                });

                if (!resp || !resp.data) throw new Error("No response from server.");
                if (resp.data.status === "error") throw new Error(resp.data.message || "Update failed.");

                // optimistic UI: update rows matched by product_sku
                var newLoc = this.modal.newLocation || "";
                for (var i = 0; i < this.items.length; i++) {
                    var it = this.items[i];
                    if (skus.indexOf(it.product_sku) !== -1) {
                        it.default_picking_location = newLoc;
                        it.is_checked = false;
                    }
                }

                this.$refs.bulkEditModal.hide();
                this.$eventHub.$emit("show_modal_message", {
                    message_content: this.modal.mode === "single" ? "✅ Location updated." : "✅ Bulk update success."
                });

                this.check_all_rows = false;
            } catch (e) {
                var msg = (e && e.message) ? e.message : String(e);
                this.$eventHub.$emit("show_modal_message", { message_content: "❌ " + msg, message_type: "error" });
            } finally {
                this.isSubmitting = false;
                if (loaderX) loaderX.hide();
            }
        }


    }
};
</script>

<style>
th,
td {
    white-space: nowrap;
}

.font-weight-500 {
    font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/components/ProductLocationBulk.vue